import { computed, flow, makeObservable, observable } from "mobx";

import { api } from "@/lib/services";
import { ApiRoutes } from "@/lib/routes";
import { Add } from "@/components/icons/IconMap";
import RiskCalendarSchema from "@/areas/risks/schemas/RiskCalendarSchema";
import { RiskCalendarEventTypes } from "@/areas/risks/components/calendar/RiskCalendarConfig";
import { getVisibleRange, parseAnchorDate, toCalendarView } from "@/components/calendar/calendarPeriod";

import { RiskConstants } from "./RiskConstants";

export default class RiskCalendarStore {
  loading = true;
  calendarEvents = null;

  // The year view is served day totals rather than rows. Only one of these is ever
  // populated, and which one tells the calendar how to read the period.
  calendarEventCounts = null;
  calendarSummary = null;

  // The period lives in the URL, so the store holds no date of its own. This counter is
  // the only local state: it lets a late response for an earlier period be dropped
  // instead of overwriting the period the reader has already paged on to.
  lastRequestId = 0;

  constructor(parentStore) {
    makeObservable(this, {
      loading: observable,
      calendarEvents: observable,
      calendarEventCounts: observable,
      calendarSummary: observable,
      actions: computed,
      events: computed,
      eventCounts: computed,
      eventSummary: computed,
      load: flow,
    });

    this.parentStore = parentStore;
  }

  get actions() {
    const list = [];

    list.push({
      id: "risks:risk:report",
      label: "Report a Risk",
      onClick: () => this.parentStore.createRiskStore.show(),
      icon: Add,
      location: "primary",
      processing: this.parentStore.createRiskStore.processing,
      disabled: this.parentStore.createRiskStore.processing,
    });
    return list;
  }

  // Returns the calendar events formatted for the calendar component
  get events() {
    return (this.calendarEvents ?? []).map((event) => {
      const eventType = Object.values(RiskCalendarEventTypes).find((type) => type.key === event.eventType);
      const start = new Date(event.startDate);
      const end = event.endDate ? new Date(event.endDate) : start;

      return {
        id: event.id,
        title: `${event.shortCode || ""}: ${event.title}`,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        color: eventType?.color,
        // The key stays on the event so the legend can tally by type; the label is display only.
        eventType: event.eventType,
        eventTypeLabel: eventType?.label ?? event.eventType,
        entityType: event.entityType,
        entityId: event.entityId,
      };
    });
  }

  // Day totals for the year view, coloured the same way the detailed events are.
  get eventCounts() {
    if (!this.calendarEventCounts) {
      return null;
    }

    return this.calendarEventCounts.map((entry) => {
      const eventType = Object.values(RiskCalendarEventTypes).find((type) => type.key === entry.eventType);

      return {
        // A plain calendar day from the server, read back as that day locally.
        date: parseAnchorDate(entry.date),
        eventType: entry.eventType,
        color: eventType?.color,
        count: entry.count,
      };
    });
  }

  get eventSummary() {
    return this.calendarSummary;
  }

  *load(query) {
    const parseResult = RiskCalendarSchema.safeParse(query);
    if (!parseResult.success) {
      console.error(parseResult.error);
      return parseResult;
    }

    const requestId = ++this.lastRequestId;

    try {
      this.loading = true;

      const storedRiskTypes = JSON.parse(localStorage.getItem(RiskConstants.StateKeys.RiskTypes)) ?? [];
      const storedRiskTypeIds = storedRiskTypes.map((riskType) => riskType.id);

      // Kept where the risk type picker keeps its own choice, so the two behave alike.
      const storedSources = JSON.parse(localStorage.getItem(RiskConstants.StateKeys.CalendarSources));
      const sources = storedSources?.map((item) => item.id);
      const updatedQuery = { ...parseResult.data.query, typeId: storedRiskTypeIds };
      const view = toCalendarView(parseResult.data.view);
      const anchorDate = parseAnchorDate(parseResult.data.timeline);
      const { startAt, endAt } = getVisibleRange(view, anchorDate);

      // A year of detailed rows grows with the number of risks and the year view would only
      // reduce them to dots, so that period is asked for day totals instead.
      const aggregated = view === "year";
      const url = aggregated ? ApiRoutes.forRiskCalendarEventCounts() : ApiRoutes.forRiskCalendarEvents();

      const response = yield api.post(url, {
        search: parseResult.data.search,
        query: updatedQuery,
        sources: sources?.length > 0 ? sources : undefined,
        startAt,
        endAt,
        // Day totals are bucketed into this zone's days, so they match what the grid draws.
        ...(aggregated ? { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone } : {}),
      });

      if (requestId !== this.lastRequestId) {
        return { success: true, stale: true };
      }

      // Clear the shape that did not arrive, so a stale period can never be read back.
      this.calendarEvents = aggregated ? null : response.data.items;
      this.calendarEventCounts = aggregated ? response.data.items : null;
      this.calendarSummary = aggregated ? response.data.summary : null;

      return { success: true, data: response.data };
    } catch (error) {
      if (requestId !== this.lastRequestId) {
        return { success: false, stale: true, error };
      }

      return { success: false, error };
    } finally {
      if (requestId === this.lastRequestId) {
        this.loading = false;
      }
    }
  }
}
